import { logInUser, logOutUser } from "@global-state/global/slice";
import { IUser } from "@interfaces/user/user";
import { authApi, logApi } from "@RTKQuery/basic";

const authLogApi = logApi.injectEndpoints({
    endpoints: (build) => ({
        userRegister: build.mutation<IUser, {
            email: string,
            password: string,
            first_name: string,
            last_name?: string,
            phone_number?: string,
            ref?: string
        }>({
            query: (data) => {
                return {
                    url: "auth/register",
                    method: "POST",
                    body: data,
                    params: data.ref ? {
                        ref: data.ref
                    } : {}
                }
            },
            async onQueryStarted(args, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;
                } catch { }
            },
        }),
        userLogin: build.mutation<IUser, {
            email: string,
            password: string,
        }>({
            query: (data) => {
                return {
                    url: "auth/login",
                    method: "POST",
                    body: data
                }
            },
            async onQueryStarted(args, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;

                    dispatch(logInUser());
                } catch { }
            },
        }),
        userLoginTelegram: build.mutation<IUser, {
            body: any,
            ref?: string
        }>({
            query: ({ body, ref }) => {
                return {
                    url: "auth/telegram",
                    method: "POST",
                    body,
                    params: ref ? { ref } : {}
                }
            },
            async onQueryStarted(args, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;

                    dispatch(logInUser());
                } catch { }
            },
        }),
        sendVerifyEmail: build.mutation<boolean, {
            email: string
        }>({
            query: (body) => ({
                url: "auth/verify_email",
                method: "POST",
                body
            })
        }),
        forgotPassword: build.mutation<boolean, {
            email: string
        }>({
            query: (body) => ({
                url: "auth/forgot_password",
                method: "POST",
                body
            })
        }),
        resetPassword: build.mutation<void, {
            token: string,
            new_password: string,
            new_password_again: string
        }>({
            query: ({ token, ...body }) => ({
                url: "auth/reset_password",
                method: "POST",
                params: {
                    token
                },
                body
            })
        })
    }),
    overrideExisting: false,
});

export const {
    useUserRegisterMutation,
    useUserLoginMutation,
    useUserLoginTelegramMutation,
    useSendVerifyEmailMutation,
    useForgotPasswordMutation,
    useResetPasswordMutation
} = authLogApi;

const authAuthApi = authApi.injectEndpoints({
    endpoints: (build) => ({
        getUser: build.query<IUser, void>({
            query: () => "auth/account",
            async onQueryStarted(args, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;

                    window.localStorage.removeItem("ref");

                    dispatch(logInUser());
                } catch {
                    dispatch(logOutUser());
                }
            }
        }),
        changeUserProfileData: build.mutation<IUser, Pick<IUser, "first_name" | "last_name" | "phone_number" | "email" | "instagram" | "telegram">>({
            query: (data) => {
                return {
                    url: "auth/change_user_data",
                    method: "POST",
                    body: data
                }
            },
            async onQueryStarted(args, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;

                    dispatch(authAuthApi.util.upsertQueryData("getUser", undefined, data));
                } catch { }
            },
        }),
        changeUserPassword: build.mutation<IUser, {
            old_password: string,
            new_password: string,
            new_password_again: string
        }>({
            query: (data) => {
                return {
                    url: "auth/change_password",
                    method: "POST",
                    body: data
                }
            },
        }),
        userLogout: build.mutation<void, void>({
            query: () => {
                return {
                    url: "auth/logout",
                    method: "POST"
                }
            },
            async onQueryStarted(args, { dispatch, queryFulfilled }) {
                try {
                    await queryFulfilled;

                    dispatch(authAuthApi.util.resetApiState());

                    dispatch(logOutUser());
                } catch { }
            },
        }),
        generateToken: build.mutation<{
            api_token: string
        }, void>({
            query: () => ({
                url: "auth/generate_api_token",
                method: "GET",
            }),
            async onQueryStarted(_, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;

                    dispatch(authAuthApi.util.updateQueryData("getUser", undefined, (draft) => {
                        draft.api_token = data.api_token;
                    }))
                } catch { }
            },
        })
    }),
    overrideExisting: false,
})

export const {
    useGetUserQuery,
    useLazyGetUserQuery,
    useChangeUserProfileDataMutation,
    useChangeUserPasswordMutation,
    useUserLogoutMutation,
    useGenerateTokenMutation
} = authAuthApi;

export default authAuthApi;